/*
 * Copyright (c) CIRROM 2026.
 */

import { useCallback, useEffect, useRef } from "react";

/**
 * Hands focus back to the control that opened an inline editor once it
 * closes.
 *
 * The pencils on this surface unmount while their editor is open, so focus
 * has nowhere to stay: closing the editor dropped it on the modal itself,
 * and a keyboard steward started the row over from the top. Each opener
 * registers under a key; closing asks for that key, and the opener - mounted
 * again by the same render - takes focus once it is back.
 *
 * An opener that comes back disabled (its write waiting out the undo
 * window) can't take focus, and is left alone rather than forced.
 */
export const useFocusReturn = () => {
  const openers = useRef(new Map<string, HTMLElement | null>());
  const pending = useRef<string | null>(null);

  useEffect(() => {
    if (pending.current === null) return;
    const opener = openers.current.get(pending.current);
    pending.current = null;
    opener?.focus();
  });

  const register = useCallback(
    (key: string) => (element: HTMLElement | null) => {
      openers.current.set(key, element);
    },
    []
  );

  const returnTo = useCallback((key: string) => {
    pending.current = key;
  }, []);

  return { register, returnTo };
};
